import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Calculator, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

import { trackEvent } from '../lib/analyticsEvents';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  insertData,
  isSupabaseConfigured,
} from '../lib/supabase';

const projectTypes = [
  { value: 'site-vitrine', label: 'Site vitrine', basePrice: 1490 },
  { value: 'site-corporate', label: 'Site corporate', basePrice: 2490 },
  { value: 'e-commerce', label: 'E-commerce', basePrice: 3490 },
  { value: 'application-web', label: 'Application web', basePrice: 5900 },
  { value: 'application-mobile', label: 'Application mobile', basePrice: 7900 },
  { value: 'refonte', label: 'Refonte de site', basePrice: 1990 },
];

const additionalServices = [
  { id: 'seo', label: 'Optimisation SEO avancée', price: 600 },
  { id: 'blog', label: 'Blog intégré', price: 400 },
  { id: 'multilingue', label: 'Site multilingue', price: 900 },
  { id: 'animations', label: 'Animations premium', price: 500 },
  { id: 'maintenance', label: 'Maintenance 1 an', price: 490 },
  { id: 'branding', label: 'Identité visuelle', price: 790 },
];

const budgetRanges = [
  { value: 'under-1500', label: 'Moins de 1 500 €' },
  { value: '1500-3000', label: '1 500 € - 3 000 €' },
  { value: '3000-5000', label: '3 000 € - 5 000 €' },
  { value: '5000-10000', label: '5 000 € - 10 000 €' },
  { value: 'over-10000', label: 'Plus de 10 000 €' },
  { value: 'undefined', label: 'À définir' },
];

const timelines = [
  { value: 'urgent', label: 'Urgent (< 2 semaines)' },
  { value: 'normal', label: 'Normal (2-4 semaines)' },
  { value: 'flexible', label: 'Flexible (1-2 mois)' },
  { value: 'no-rush', label: 'Pas de deadline' },
];
const quoteFaqs = [
  {
    q: 'Le devis est-il gratuit ?',
    a: 'Oui, la demande de devis est gratuite et sans engagement.',
  },
  {
    q: 'L’estimation du simulateur est-elle définitive ?',
    a: 'Non, le simulateur donne une estimation indicative. Un devis personnalisé est ensuite établi selon votre projet.',
  },
  {
    q: 'Sous quel délai recevez-vous une réponse ?',
    a: 'Nous répondons généralement sous 24 à 48 heures ouvrées.',
  },
  {
    q: 'Pouvez-vous adapter le projet à mon budget ?',
    a: 'Oui, nous proposons des solutions sur mesure selon vos objectifs, vos priorités et votre budget.',
  },
];

export const Quote = () => {
  const [activeTab, setActiveTab] = useState('form');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    services: [],
  });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    trackEvent('view_quote_page', {
      page_path: window.location.pathname,
    });
  }, []);

  const [simProjectType, setSimProjectType] = useState('site-vitrine');
  const [simPages, setSimPages] = useState([5]);
  const [simServices, setSimServices] = useState([]);

  const pageTitle =
    'Demande de devis Weblax | Devis gratuit pour site internet, SEO, e-commerce et applications';
  const pageDescription =
    'Demandez un devis gratuit à Weblax pour votre création de site internet, refonte, e-commerce, SEO, application web ou mobile. Simulateur de prix et accompagnement sur mesure.';
  const pageUrl = 'https://weblax.fr/devis';
  const pageImage = 'https://weblax.fr/og-image.jpg';

  const webPageStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Demande de devis Weblax',
    url: pageUrl,
    description: pageDescription,
  };

  const serviceStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Demande de devis Weblax',
    provider: {
      '@type': 'Organization',
      name: 'Weblax',
      url: 'https://weblax.fr',
    },
    areaServed: 'FR',
    serviceType: 'Création de site internet, SEO, e-commerce, applications web et mobiles',
    url: pageUrl,
    description: pageDescription,
  };

  const faqStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: quoteFaqs.map((faq) => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  const organizationStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Weblax',
    url: 'https://weblax.fr',
    logo: 'https://weblax.fr/logo.png',
    email: 'contact@weblax.fr',
    telephone: '+33685802016',
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Paris',
      addressCountry: 'FR',
    },
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceToggle = (serviceId) => {
    setFormData((prev) => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter((s) => s !== serviceId)
        : [...prev.services, serviceId],
    }));
  };

  const handleSimServiceToggle = (serviceId) => {
    setSimServices((prev) =>
      prev.includes(serviceId)
        ? prev.filter((s) => s !== serviceId)
        : [...prev, serviceId]
    );
  };

  const estimatedPrice = useMemo(() => {
    const baseProject = projectTypes.find((p) => p.value === simProjectType);
    if (!baseProject) return 0;

    let total = baseProject.basePrice;

    const isWebsiteProject =
      simProjectType === 'site-vitrine' ||
      simProjectType === 'site-corporate' ||
      simProjectType === 'e-commerce' ||
      simProjectType === 'refonte';

    if (isWebsiteProject) {
      const pagesCount = simPages[0];
      if (pagesCount > 5) {
        total += (pagesCount - 5) * 120;
      }
    }

    simServices.forEach((serviceId) => {
      const service = additionalServices.find((s) => s.id === serviceId);
      if (service) {
        total += service.price;
      }
    });

    return total;
  }, [simProjectType, simPages, simServices]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (
      !formData.firstName ||
      !formData.email ||
      !formData.projectType ||
      !formData.description
    ) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setIsLoading(true);

    try {
      const payload = {
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        project_type: formData.projectType,
        budget: formData.budget,
        timeline: formData.timeline,
        description: formData.description,
        services: formData.services,
        status: 'new',
      };

      const { data: quoteRow, error, demo } = await insertData('quote', payload);

      if (error) {
        throw error;
      }

      const quoteForEmail = quoteRow || payload;

      if (!demo && isSupabaseConfigured) {
        try {
          await fetch(
            `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-quote-email`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
              },
              body: JSON.stringify({
                quote: quoteForEmail,
              }),
            }
          );
        } catch (err) {
          console.error('Erreur envoi email devis', err);
          toast.error("La demande a été enregistrée, mais l'email n'a pas pu être envoyé.");
          return;
        }
      }

      if (demo) {
        toast.success('Demande envoyée ! (Mode démo - Supabase non configuré)');
      } else {
        toast.success('Votre demande de devis a bien été envoyée.');
      }

      trackEvent('quote_form_submit', {
        form_type: 'quote',
        project_type: formData.projectType || 'unknown',
        budget_range: formData.budget || 'unknown',
        timeline: formData.timeline || 'unknown',
        page_path: window.location.pathname,
      });

      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        budget: '',
        timeline: '',
        description: '',
        services: [],
      });
    } catch (err) {
      console.error(err);
      toast.error('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="devis site internet, devis gratuit site web, prix création site internet, devis SEO, devis e-commerce, devis application web, simulateur tarif site internet, Weblax"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
      </Helmet>

      <div data-testid="quote-page">
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28 text-center">
              <p className="weblax-caption text-primary mb-4">Demande de devis</p>
              <h1 className="weblax-heading-display mb-6 max-w-3xl mx-auto">
                Obtenez un <span className="weblax-gradient-text">devis gratuit</span>
              </h1>
              <p className="weblax-body-lg max-w-2xl mx-auto">
                Décrivez votre projet et recevez une proposition personnalisée adaptée
                à vos besoins et votre budget.
              </p>
            </div>
          </div>
        </section>

        <section className="weblax-section">
          <div className="weblax-container">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
              <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12">
                <TabsTrigger value="form" data-testid="tab-form">
                  Demande de devis
                </TabsTrigger>
                <TabsTrigger value="simulator" data-testid="tab-simulator">
                  Simulateur
                </TabsTrigger>
              </TabsList>

              <TabsContent value="form">
                <Card className="weblax-card max-w-4xl mx-auto">
                  <CardContent className="p-6 md:p-8">
                    <form onSubmit={handleSubmit} className="space-y-8">
                      <div>
                        <h2 className="font-semibold text-lg mb-4">Vos informations</h2>
                        <div className="grid md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">Prénom *</Label>
                            <Input
                              id="firstName"
                              name="firstName"
                              value={formData.firstName}
                              onChange={handleChange}
                              placeholder="Votre prénom"
                              data-testid="quote-firstname"
                              className="weblax-input"
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="lastName">Nom</Label>
                            <Input
                              id="lastName"
                              name="lastName"
                              value={formData.lastName}
                              onChange={handleChange}
                              placeholder="Votre nom"
                              data-testid="quote-lastname"
                              className="weblax-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              placeholder="votre@email.com"
                              data-testid="quote-email"
                              className="weblax-input"
                              required
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="phone">Téléphone</Label>
                            <Input
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleChange}
                              placeholder="+33 6 12 34 56 78"
                              data-testid="quote-phone"
                              className="weblax-input"
                            />
                          </div>

                          <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="company">Entreprise</Label>
                            <Input
                              id="company"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              placeholder="Nom de votre entreprise"
                              data-testid="quote-company"
                              className="weblax-input"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <h2 className="font-semibold text-lg mb-4">Votre projet</h2>
                        <div className="grid md:grid-cols-2 gap-4 mb-4">
                          <div className="space-y-2">
                            <Label htmlFor="projectType">Type de projet *</Label>
                            <Select
                              value={formData.projectType}
                              onValueChange={(v) => handleSelectChange('projectType', v)}
                            >
                              <SelectTrigger
                                data-testid="quote-project-type"
                                className="weblax-input"
                              >
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent>
                                {projectTypes.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    {type.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="budget">Budget estimatif</Label>
                            <Select
                              value={formData.budget}
                              onValueChange={(v) => handleSelectChange('budget', v)}
                            >
                              <SelectTrigger
                                data-testid="quote-budget"
                                className="weblax-input"
                              >
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent>
                                {budgetRanges.map((range) => (
                                  <SelectItem key={range.value} value={range.value}>
                                    {range.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2 md:col-span-2">
                            <Label htmlFor="timeline">Délais souhaités</Label>
                            <Select
                              value={formData.timeline}
                              onValueChange={(v) => handleSelectChange('timeline', v)}
                            >
                              <SelectTrigger
                                data-testid="quote-timeline"
                                className="weblax-input"
                              >
                                <SelectValue placeholder="Sélectionner" />
                              </SelectTrigger>
                              <SelectContent>
                                {timelines.map((timeline) => (
                                  <SelectItem key={timeline.value} value={timeline.value}>
                                    {timeline.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="description">Description du projet *</Label>
                          <Textarea
                            id="description"
                            name="description"
                            value={formData.description}
                            onChange={handleChange}
                            placeholder="Décrivez votre projet, vos objectifs, vos attentes..."
                            data-testid="quote-description"
                            className="weblax-input min-h-[150px]"
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <h2 className="font-semibold text-lg mb-4">
                          Services complémentaires
                        </h2>
                        <div className="grid sm:grid-cols-2 gap-3">
                          {additionalServices.map((service) => (
                            <div
                              key={service.id}
                              className="flex items-center space-x-3 p-3 rounded-lg border border-border hover:border-primary/30 transition-colors"
                            >
                              <Checkbox
                                id={service.id}
                                checked={formData.services.includes(service.id)}
                                onCheckedChange={() => handleServiceToggle(service.id)}
                              />
                              <Label
                                htmlFor={service.id}
                                className="flex-1 cursor-pointer text-sm"
                              >
                                {service.label}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </div>

                      <Button
                        type="submit"
                        disabled={isLoading}
                        data-testid="quote-submit"
                        className="weblax-btn-primary w-full md:w-auto"
                      >
                        {isLoading ? 'Envoi en cours...' : 'Envoyer ma demande'}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="simulator">
                <div className="grid lg:grid-cols-2 gap-8 max-w-5xl mx-auto">
                  <Card className="weblax-card">
                    <CardContent className="p-6 md:p-8">
                      <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                          <Calculator className="w-5 h-5 text-primary" />
                        </div>
                        <h2 className="font-semibold text-lg">Configurez votre projet</h2>
                      </div>

                      <div className="space-y-6">
                        <div className="space-y-2">
                          <Label>Type de projet</Label>
                          <Select value={simProjectType} onValueChange={setSimProjectType}>
                            <SelectTrigger
                              data-testid="sim-project-type"
                              className="weblax-input"
                            >
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {projectTypes.map((type) => (
                                <SelectItem key={type.value} value={type.value}>
                                  {type.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {(simProjectType === 'site-vitrine' ||
                          simProjectType === 'site-corporate' ||
                          simProjectType === 'e-commerce' ||
                          simProjectType === 'refonte') && (
                            <div className="space-y-4">
                              <div className="flex justify-between">
                                <Label>Nombre de pages</Label>
                                <span className="text-sm font-medium">{simPages[0]} pages</span>
                              </div>
                              <Slider
                                value={simPages}
                                onValueChange={setSimPages}
                                min={1}
                                max={30}
                                step={1}
                                data-testid="sim-pages-slider"
                              />
                            </div>
                          )}

                        <div className="space-y-3">
                          <Label>Services additionnels</Label>
                          {additionalServices.map((service) => (
                            <div
                              key={service.id}
                              className="flex items-center justify-between p-3 rounded-lg border border-border hover:border-primary/30 transition-colors"
                            >
                              <div className="flex items-center gap-3">
                                <Checkbox
                                  id={`sim-${service.id}`}
                                  checked={simServices.includes(service.id)}
                                  onCheckedChange={() => handleSimServiceToggle(service.id)}
                                />
                                <Label
                                  htmlFor={`sim-${service.id}`}
                                  className="cursor-pointer text-sm"
                                >
                                  {service.label}
                                </Label>
                              </div>
                              <span className="text-sm text-muted-foreground">
                                +{service.price} €
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="weblax-card bg-primary/5 border-primary/20">
                    <CardContent className="p-6 md:p-8">
                      <h2 className="font-semibold text-lg mb-6">Estimation</h2>

                      <div className="space-y-4 mb-8">
                        <div className="flex justify-between py-2 border-b border-border">
                          <span className="text-muted-foreground">
                            {projectTypes.find((p) => p.value === simProjectType)?.label}
                          </span>
                          <span className="font-medium">
                            {projectTypes.find((p) => p.value === simProjectType)?.basePrice} €
                          </span>
                        </div>

                        {(simProjectType === 'site-vitrine' ||
                          simProjectType === 'site-corporate' ||
                          simProjectType === 'e-commerce' ||
                          simProjectType === 'refonte') &&
                          simPages[0] > 5 && (
                            <div className="flex justify-between py-2 border-b border-border">
                              <span className="text-muted-foreground">
                                Pages supplémentaires ({simPages[0] - 5})
                              </span>
                              <span className="font-medium">
                                +{(simPages[0] - 5) * 120} €
                              </span>
                            </div>
                          )}

                        {simServices.map((serviceId) => {
                          const service = additionalServices.find((s) => s.id === serviceId);
                          return service ? (
                            <div
                              key={serviceId}
                              className="flex justify-between py-2 border-b border-border"
                            >
                              <span className="text-muted-foreground">{service.label}</span>
                              <span className="font-medium">+{service.price} €</span>
                            </div>
                          ) : null;
                        })}
                      </div>

                      <div className="bg-background rounded-xl p-6 text-center mb-6">
                        <p className="text-sm text-muted-foreground mb-2">Estimation totale</p>
                        <p className="text-4xl font-bold text-primary">
                          {estimatedPrice.toLocaleString('fr-FR')} €
                        </p>
                        <p className="text-xs text-muted-foreground mt-2">
                          *Cette estimation est indicative et ne constitue pas un devis définitif
                        </p>
                      </div>

                      <Link to="/devis" onClick={() => setActiveTab('form')}>
                        <Button className="weblax-btn-primary w-full">
                          Demander un devis précis
                          <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                      </Link>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="text-center max-w-2xl mx-auto mb-8">
              <h2 className="weblax-heading-2 mb-4">Pourquoi nous faire confiance</h2>
            </div>
            <div className="grid md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              {[
                { title: 'Devis gratuit', desc: 'Sans engagement' },
                { title: 'Réponse rapide', desc: 'Sous 24-48h' },
                { title: 'Prix transparent', desc: 'Pas de frais cachés' },
                { title: 'Sur mesure', desc: 'Adapté à vos besoins' },
              ].map((item, index) => (
                <div key={index} className="text-center">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="font-semibold mb-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="weblax-section">
          <div className="weblax-container">
            <div className="max-w-3xl mx-auto grid gap-6">
              {quoteFaqs.map((faq, index) => (
                <Card key={index} className="weblax-card">
                  <CardContent className="p-6">
                    <h2 className="font-semibold mb-2">{faq.q}</h2>
                    <p className="text-muted-foreground text-sm">{faq.a}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Quote;